import React, { useEffect } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import { Clear, LocationOn, Close } from '@mui/icons-material';
import { MiniPanelProps, ExpandedPanelProps } from '../../../../types/steps';
import MiniPanel from './MiniPanel';
import ExpandedPanel from './ExpandedPanel';
import { useMap, usePinCount } from '../../../../state/map';
import { removeAllPins, getPins, removePin } from '../../pins';

export const PinsMiniPanel: React.FC<MiniPanelProps> = ({ isActive, onExpand }) => {
  const map = useMap();
  const pinCount = usePinCount(); // Use reactive pin count from Redux

  useEffect(() => {
    window.pinsEnabled = isActive;
    return () => {
      window.pinsEnabled = false;
    };
  }, [isActive]);

  const handleClearPins = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the panel
    removeAllPins(map);
  };

  return (
    <MiniPanel isActive={isActive} onExpand={onExpand}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOn sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {pinCount} pin{pinCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
        {pinCount > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Clear />}
            onClick={handleClearPins}
            sx={{ 
              borderRadius: '16px',
              textTransform: 'none',
              fontSize: '12px'
            }}
          >
            Clear pins
          </Button>
        )}
      </Box>
    </MiniPanel>
  );
};

export const PinsExpandedPanel: React.FC<ExpandedPanelProps> = ({ 
  isActive, 
  onMinimize, 
  onClose 
}) => {
  const map = useMap();
  const pinCount = usePinCount(); // Use reactive pin count from Redux
  const pins = map ? getPins(map) : []; // Still get pins for detailed display

  useEffect(() => {
    window.pinsEnabled = true;
    return () => {
      window.pinsEnabled = false;
    };
  }, []);

  const handleClearAll = () => {
    removeAllPins(map);
  };

  const handleRemovePin = (pinToRemove: any) => {
    removePin(map, pinToRemove);
  };

  return (
    <ExpandedPanel 
      isActive={isActive} 
      onMinimize={onMinimize} 
      onClose={onClose}
      title="Manage Pins"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Pin Stats */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'background.default',
          borderRadius: '12px'
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {pinCount} Pin{pinCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {/* Instructions */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'primary.light',
          borderRadius: '12px'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            💡 Click anywhere on the map to add a pin
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Maximum 20 pins allowed
          </Typography>
        </Box>

        {/* Pin List */}
        {pinCount > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Your Pins
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {pins.map((pin, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationOn sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Pin {index + 1}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pin.lat.toFixed(4)}, {pin.lng.toFixed(4)}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePin(pin)}
                    sx={{
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Quick Actions */}
        {pins.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={handleClearAll}
              sx={{ 
                borderRadius: '20px',
                textTransform: 'none'
              }}
            >
              Clear All Pins
            </Button>
          </Box>
        )}
      </Box>
    </ExpandedPanel>
  );
};
